// import { User } from 'firebase/auth';
import React from 'react';
import { Component } from 'react';
import {
    AudioInputDevice,
    DashboardFeature,
    HealthcareProvider,
    ProviderLicenseStatus,
    TherapySession,
    TherapySessionParticipant,
    User,
} from '../interfaces';
import { Menu } from './Menu';
import { PresenterAndView } from './Presentation';
import { Presented } from './Presented';
import { Theme } from '../Theme';
import { Button } from './Button';
import { ClinicalNotesEditor } from './ClinicalNotesEditor';
import { HealthcareProviderSelector } from './HealthcareProviderSelector';
import { isRootOrSuperRole } from '../DashboardAccess';
import { getRequiredAdditionalLicenses } from '../saas/licenseUsage';
import { DashboardLoading } from './DashboardLoading';
import { SessionRecorderControl } from './SessionRecorderControl';

export type MenuOption = {
    label: string;
    icon: {
        normal: string;
        selected: string;
    };
    id: string;
    presenterAndView: PresenterAndView;
    feature?: DashboardFeature;
    isDevelopment?: boolean;
    rootOrSuperOnly?: boolean;
};

type Props = {
    loading: boolean;
    user: User;
    healthcareProviderId: string;
    healthcareProviders: HealthcareProvider[];
    selectableHealthcareProviders: HealthcareProvider[];
    canSelectHealthcareProvider: boolean;
    clientCountByProviderId: Record<string, number>;
    licenseStatusByProviderId: Record<string, ProviderLicenseStatus | undefined>;
    sessionRecording: {
        status: 'idle' | 'recording' | 'saving';
        durationSeconds: number;
        clinicalNotes: string;
        areClinicalNotesExpanded: boolean;
        participants: Array<TherapySessionParticipant>;
    };
    canRecordSessions: boolean;
    recordingParticipantOptions: Array<TherapySessionParticipant>;
    defaultRecordingParticipant?: TherapySessionParticipant;
    defaultRecordingClientParticipant?: TherapySessionParticipant;
    audioInputDevices: Array<AudioInputDevice>;
    selectedAudioInputDeviceId: string;
    unassignedRecordingSessions: Array<TherapySession>;
    onStartSessionRecording: (participants: Array<TherapySessionParticipant>) => Promise<boolean>;
    onDeleteUnassignedSession: (session: TherapySession) => Promise<void>;
    onEnsureSessionWaveform: (sessionId: string) => Promise<void>;
    onStopSessionRecording: () => void;
    onToggleSessionRecordingClinicalNotes: () => void;
    onSessionRecordingClinicalNotesChange: (clinicalNotes: string) => void;
    onAudioInputDeviceChange: (deviceId: string) => void;
    onRefreshAudioInputDevices: () => Promise<void>;
    onHealthcareProviderChange: (providerId: string) => void;
    onHealthcareProviderSelectorOpen: () => void;
    onLogout: () => void;
    menuOptions: Array<MenuOption>;
    selectedMenuOptionIndex: number;
    onMenuOptionSelect: (optionId: string) => void;
};

// Show the configured provider logo, falling back to its name when no usable logo URL exists.
export const HealthcareProviderBrand: React.FC<{ healthcareProvider?: HealthcareProvider }> = ({ healthcareProvider }) => {
    if (!healthcareProvider) {
        return null;
    }

    const logoUrl = healthcareProvider.logoUrl?.trim();
    if (logoUrl) {
        return <img src={logoUrl} alt={healthcareProvider.name} style={styles.healthcareProviderLogo} />;
    }

    const providerName = healthcareProvider.name.trim();
    return providerName ? <span style={styles.healthcareProviderName}>{providerName}</span> : null;
};

export class DashboardView extends Component<Props> {

    private renderHeader(): React.ReactNode {
        if (this.props.loading) {
            return null;
        }

        const menuOption = this.props.menuOptions[this.props.selectedMenuOptionIndex];

        return (
            <div style={styles.header}>
                    
                {/* left side of the header */}
                <div style={styles.headerContent}>
                    <h2 style={styles.title}>{menuOption.label}</h2>
                </div>

                {/* right side of the header */}
                <div style={styles.userContainer}>

                    {this.props.user.authenticatedUser?.email}

                    {/* vertical separator line */}
                    <div style={styles.separator}></div>
                    
                    {this.props.canSelectHealthcareProvider ? (
                        <HealthcareProviderSelector
                            healthcareProviderId={this.props.healthcareProviderId}
                            healthcareProviders={this.props.selectableHealthcareProviders}
                            clientCountByProviderId={this.props.clientCountByProviderId}
                            licenseStatusByProviderId={this.props.licenseStatusByProviderId}
                            onChange={this.props.onHealthcareProviderChange}
                            onOpen={this.props.onHealthcareProviderSelectorOpen}
                            style={styles.providerSelector}
                        />
                    ) : (
                        this.renderHealthcareProviderLogo()
                    )}

                    <SessionRecorderControl
                        healthcareProviderId={this.props.healthcareProviderId}
                        canRecord={this.props.canRecordSessions}
                        status={this.props.sessionRecording.status}
                        participantOptions={this.props.recordingParticipantOptions}
                        defaultParticipant={this.props.defaultRecordingParticipant}
                        defaultClientParticipant={this.props.defaultRecordingClientParticipant}
                        audioInputDevices={this.props.audioInputDevices}
                        selectedAudioInputDeviceId={this.props.selectedAudioInputDeviceId}
                        unassignedSessions={this.props.unassignedRecordingSessions}
                        onAudioInputDeviceChange={this.props.onAudioInputDeviceChange}
                        onRefreshAudioInputDevices={this.props.onRefreshAudioInputDevices}
                        onRecord={this.props.onStartSessionRecording}
                        onDeleteUnassignedSession={this.props.onDeleteUnassignedSession}
                        onEnsureSessionWaveform={this.props.onEnsureSessionWaveform}
                    />
                </div>
            </div>
        );
    };

    private renderHealthcareProviderLogo(): React.ReactNode {
        const healthcareProvider = this.props.healthcareProviders.find(hp => hp.id === this.props.healthcareProviderId);
        return <HealthcareProviderBrand healthcareProvider={healthcareProvider} />;
    }

    // Keep family overage visible while the 30-day regularisation window is open.
    private renderLicenseWarning(): React.ReactNode {
        const provider = this.props.healthcareProviders.find(
            (candidate) => candidate.id === this.props.healthcareProviderId
        );
        const usage = provider?.licenseUsage;
        if (provider?.commercialModel !== 'self_service' || !usage ||
            (usage.availableLicenses >= 0 && usage.outstandingOverageLicenses <= 0)) {
            return null;
        }

        const overage = getRequiredAdditionalLicenses(usage);
        const regularisationDate = usage.overageGraceEndsAt ?
            new Date(usage.overageGraceEndsAt).toLocaleDateString('en-GB') : '';
        const canPurchase = provider.entitlement?.billingOwnerUserId ===
            this.props.user.authenticatedUser?.uid || isRootOrSuperRole(this.props.user.dashboardRole);

        return (
            <div role='alert' style={overage > 0 ? styles.licenseOverageBanner : styles.licenseWarningBanner}>
                <strong>
                    {overage > 0 ?
                        `You are ${overage} family license${overage === 1 ? '' : 's'} over your allowance.` :
                        'You have used all family licenses in your allowance.'}
                </strong>
                <span>
                    {canPurchase ?
                        (provider.selfServicePlanCode === 'starter' ?
                            ` Upgrade to Premium${regularisationDate ? ` by ${regularisationDate}` : ' within 30 days'} to keep dashboard service active.` :
                            ` Increase your Premium subscription units${regularisationDate ? ` by ${regularisationDate}` : ' within 30 days'} to keep dashboard service active.`) :
                        ` Contact your billing owner or an organisation Admin to increase capacity${regularisationDate ? ` by ${regularisationDate}` : ' within 30 days'}.`}
                    {' '}Existing families and valid activation keys keep access to SuperPenguin.
                </span>
            </div>
        );
    }

    private renderMenu(): React.ReactNode {
        const menuOptions = this.props.menuOptions.map(option => ({
            label: option.label,
            icon: option.icon,
            id: option.id,
            isDevelopment: !!option.isDevelopment,
            onSelect: () => {
                this.props.onMenuOptionSelect(option.id);
            },
        }));
        return <Menu options={menuOptions} selectedMenuOptionIndex={this.props.selectedMenuOptionIndex} onLogout={this.props.onLogout} />;
    }

    private renderContent(): React.ReactNode {
        const menuOption = this.props.menuOptions[this.props.selectedMenuOptionIndex];
        if (menuOption) {
            const presenter = menuOption.presenterAndView?.presenter;
            const presentedComponent = menuOption.presenterAndView?.presentedViewClass;
            if (presenter && presentedComponent) {
                return <Presented presenter={presenter} presentedComponent={presentedComponent} />;
            }
        }
        return <div>No content</div>;
    }

    // Format the global recorder duration as a compact timer.
    private formatRecordingDuration(durationSeconds: number): string {
        const hours = Math.floor(durationSeconds / 3600);
        const minutes = Math.floor((durationSeconds % 3600) / 60);
        const seconds = durationSeconds % 60;

        if (hours > 0) {
            return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }

        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    // Render the persistent recording controls above the dashboard header.
    private renderSessionRecordingPanel(): React.ReactNode {
        if (!this.props.canRecordSessions || !this.props.sessionRecording || this.props.sessionRecording.status === 'idle') {
            return null;
        }

        const isSaving = this.props.sessionRecording.status === 'saving';
        const isClinicalNotesExpanded = this.props.sessionRecording.areClinicalNotesExpanded;
        const participantNames = this.props.sessionRecording.participants
            .map((participant) => participant.displayName)
            .filter(Boolean);

        return (
            <div style={styles.recordingPanel} role='status'>
                <div style={styles.recordingPanelTopRow}>
                    <div style={styles.recordingInfo}>
                        <span style={styles.recordingLight} aria-hidden='true' />
                        <span style={styles.recordingLabel}>{isSaving ? 'Saving recording' : 'Recording session'}</span>
                        {participantNames.length > 0 && (
                            <span style={styles.recordingParticipants} title={participantNames.join(', ')}>
                                {participantNames.join(', ')}
                            </span>
                        )}
                        <span style={styles.recordingDuration} aria-hidden='true'>
                            {this.formatRecordingDuration(this.props.sessionRecording.durationSeconds)}
                        </span>
                    </div>
                    <div style={styles.recordingActions}>
                        <button
                            type='button'
                            onClick={this.props.onToggleSessionRecordingClinicalNotes}
                            style={styles.recordingNotesToggleButton}
                            aria-expanded={isClinicalNotesExpanded}
                            aria-controls='session-recording-clinical-notes'
                            disabled={isSaving}
                        >
                            <span>Clinical notes</span>
                            <span
                                style={{
                                    ...styles.recordingNotesChevron,
                                    transform: isClinicalNotesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                                aria-hidden='true'
                            >
                                <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                                    <polyline
                                        points='6 9 12 15 18 9'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                    />
                                </svg>
                            </span>
                        </button>
                        <Button
                            onClick={this.props.onStopSessionRecording}
                            label={isSaving ? 'Saving...' : 'Stop'}
                            type='secondary'
                            disabled={isSaving}
                            style={styles.stopRecordingButton}
                        />
                    </div>
                </div>
                {isClinicalNotesExpanded && (
                    <div id='session-recording-clinical-notes'>
                        <ClinicalNotesEditor
                            value={this.props.sessionRecording.clinicalNotes}
                            onChange={this.props.onSessionRecordingClinicalNotesChange}
                            markerTimestampMs={this.props.sessionRecording.durationSeconds * 1000}
                            placeholder='Add clinical notes for this session.'
                            disabled={isSaving}
                        />
                    </div>
                )}
            </div>
        );
    }

    render() {
        if (this.props.loading) {
            return <DashboardLoading />;
        }
        return (
            <div className='dashboard-view' style={styles.dashboard}>

                {/* vertical menu */}
                {this.renderMenu()}

                <div style={styles.rightContainer}>

                    {/* active session recording */}
                    {this.renderSessionRecordingPanel()}

                    {/* header */}
                    {this.renderHeader()}

                    {/* non-blocking self-service license warning */}
                    {this.renderLicenseWarning()}

                    {/* body with vertical menu and content */}
                    <div style={styles.bodyContainer}>
                        {this.renderContent()}
                    </div>
                </div>

            </div>
        );
    }
}

const styles = {
    dashboard: {
        display: 'flex',
        flexDirection: 'row' as 'row',
        height: '100vh',
        minWidth: '700px',
        position: 'relative' as 'relative',
        overflow: 'hidden' as const,
    },
    rightContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as 'column',
        backgroundColor: Theme.Colours.background,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden' as const,
    },
    licenseWarningBanner: {
        ...Theme.Fonts.body,
        margin: '0 16px 12px 16px',
        padding: '10px 14px',
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap' as const,
        color: '#6b4e00',
        backgroundColor: '#fff8d9',
        border: '1px solid #e8cc67',
        borderRadius: '8px',
        flexShrink: 0,
    },
    licenseOverageBanner: {
        ...Theme.Fonts.body,
        margin: '0 16px 12px 16px',
        padding: '10px 14px',
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap' as const,
        color: '#8a1c12',
        backgroundColor: '#fff0ed',
        border: '1px solid #e6a39b',
        borderRadius: '8px',
        flexShrink: 0,
    },
    bodyContainer: {
        display: 'flex',
        flexDirection: 'row' as 'row',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden' as const,
    },
    recordingPanel: {
        minHeight: '56px',
        flexShrink: 0,
        margin: '0 16px 16px 16px',
        padding: '10px 16px',
        boxSizing: 'border-box' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '10px',
        backgroundColor: Theme.Colours.workArea,
        border: Theme.Colours.border,
        borderRadius: '8px',
    },
    recordingPanelTopRow: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
    },
    recordingInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: 0,
    },
    recordingLight: {
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#d32f2f',
        boxShadow: '0 0 0 4px rgba(211, 47, 47, 0.12)',
        flexShrink: 0,
    },
    recordingLabel: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        fontWeight: 'bold' as const,
        whiteSpace: 'nowrap' as const,
    },
    recordingParticipants: {
        ...Theme.Fonts.body,
        maxWidth: '360px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        color: '#5f5f5f',
    },
    recordingDuration: {
        ...Theme.Fonts.body,
        color: Theme.Colours.text,
        fontVariantNumeric: 'tabular-nums' as const,
        minWidth: '48px',
    },
    recordingActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        marginLeft: 'auto',
        flexShrink: 0,
    },
    recordingNotesToggleButton: {
        ...Theme.Fonts.body,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
        height: '34px',
        padding: '6px 10px 6px 12px',
        border: Theme.Colours.border,
        borderRadius: '6px',
        backgroundColor: Theme.Colours.workArea,
        color: Theme.Colours.text,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontWeight: 600,
    },
    recordingNotesChevron: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s ease',
    },
    stopRecordingButton: {
        height: '34px',
        padding: '6px 14px',
    },
    footer: {
        backgroundColor: '#fff',
        padding: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    footerText: {
        paddingLeft: '10px',
        fontSize: '12px',
        color: '#000',
    },
    footerImage: {
        width: '150px',
        paddingRight: '10px',
    },

    // header
    header: {
        height: '120px',
        flexShrink: 0,
        flexDirection: 'row' as 'row',
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: Theme.Colours.background,
        alignItems: 'center',
    },
    headerContent: {
        flexDirection: 'row' as 'row',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '16px',
    },
    title: {
        ...Theme.Fonts.h1,
        color: Theme.Colours.text,
    },
    healthcareProviderLogo: {
        height: '60px',
        paddingRight: '10px',
    },
    healthcareProviderName: {
        ...Theme.Fonts.h2,
        maxWidth: '260px',
        color: Theme.Colours.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
    },

    // user info
    userContainer: {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
        marginRight: '16px',
    },
    separator: {
        width: '1px',
        height: '33px',
        backgroundColor: '#C3C3C3',
        margin: '0px 24px',
    },


    // Root/Super provider selector; its named feature is independent from Development.
    providerSelector: {
        marginTop: '10px',
        marginLeft: '0px',
        marginRight: '16px',
    },
    healthcareProvider: {
        color: Theme.Colours.text,
        margin: '10px 0px 0px 0px',
        textAlign: 'left' as const,
        fontSize: '0.9em',
        alignItems: 'flex-start',
    },

};
